import "./CustomerFormFilters.css";
import { Popover } from "@headlessui/react";
import { AdjustmentsHorizontalIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import type { FormType } from "../../types/forms";
import { statusLabels } from "../../shared/customerForms";
import { secondaryClass } from "./FormControls";
import FormFilterSelect from "./FormFilterSelect";
import AdvancedFilters from "./AdvancedFormFilters";
import FormFilterSummary, { FormTypeFilter } from "./FormFilterSummary";
import type { FormFilterProps } from "./formFilterTypes";

export default function CustomerFormFilters({
  params,
  options,
  customerId,
  onChange,
  onApply,
  onClear,
}: FormFilterProps) {
  const rawType = params.get("type");
  const type: FormType | null = rawType === "credit" || rawType === "demo" ? rawType : null;
  const statuses = options
    ? type
      ? options.statuses[type]
      : Array.from(new Set([...options.statuses.credit, ...options.statuses.demo]))
    : [];
  const from = params.get("date_from");
  const to = params.get("date_to");
  const advancedCount =
    Number(!customerId && Boolean(params.get("customer_id"))) +
    Number(Boolean(params.get("created_by"))) +
    Number(Boolean(from || to));
  return (
    <div className="space-y-4">
      <FormTypeFilter params={params} onChange={onChange} />
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-0 basis-full sm:flex-1 sm:basis-0">
          <label htmlFor="forms-search" className="sr-only">
            Search forms
          </label>
          <MagnifyingGlassIcon
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-gray-400"
          />
          <input
            id="forms-search"
            type="search"
            placeholder="Search reference, customer, invoice, event…"
            value={params.get("search") || ""}
            onChange={(event) => onChange("search", event.target.value)}
            className="min-h-10 w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm placeholder:text-gray-400 focus:border-primary-500 focus:ring-primary-500"
          />
        </div>
        <div className="min-w-0 flex-1 sm:w-44 sm:flex-none">
          <FormFilterSelect
            compact
            label="Status"
            value={params.get("status") || ""}
            options={[
              { value: "", label: "All statuses" },
              ...statuses.map((value) => ({ value, label: statusLabels[value] })),
            ]}
            onChange={(value) => onChange("status", value)}
            disabled={!options}
          />
        </div>
        <Popover className="relative">
          <Popover.Button className={`${secondaryClass} min-h-10 gap-2 border-gray-200`}>
            <AdjustmentsHorizontalIcon aria-hidden="true" className="h-4 w-4" />
            Filters
            {advancedCount > 0 && (
              <span className="rounded bg-primary-50 px-1.5 text-xs text-primary-700">
                {advancedCount}
              </span>
            )}
          </Popover.Button>
          <Popover.Overlay className="fixed inset-0 z-20 bg-gray-900/20 sm:hidden" />
          <Popover.Panel
            anchor={{ to: "bottom end", gap: 8, padding: 16 }}
            className="forms-filter-panel z-30 w-[26rem] max-w-[calc(100vw-2rem)] overflow-y-auto rounded-xl border border-gray-200 bg-white"
          >
            {({ close }) => (
              <AdvancedFilters
                params={params}
                options={options}
                customerId={customerId}
                onApply={onApply}
                close={close}
              />
            )}
          </Popover.Panel>
        </Popover>
      </div>
      <FormFilterSummary
        params={params}
        options={options}
        customerId={customerId}
        onChange={onChange}
        onApply={onApply}
        onClear={onClear}
      />
    </div>
  );
}
